// =============================================
// src/utils/constants.js
// =============================================

export const styles = {
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "32px 24px",
    minHeight: "100vh",
  },
  header: {
    marginBottom: "-60px",
    padding: "48px 24px 86px 56px",
    background: "linear-gradient(135deg, #2c3e50 50%, #094e7cff 100%)",
    position: "relative",
    overflow: "visible",
    border: "1px solid rgba(255,255,255,0.12)",
  },
  title: {
    fontSize: "32px",
    fontWeight: "700",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    margin: 0,
    color: "#ffffff",
    letterSpacing: "-0.5px",
  },
  subtitle: {
    fontSize: "16px",
    color: "#dae2eeff",
    marginTop: "8px",
    fontWeight: "400",
  },
  card: {
    background: "#ffffff",
    backdropFilter: "blur(20px) saturate(1.8)",
    WebkitBackdropFilter: "blur(20px) saturate(1.8)",
    borderRadius: "20px",
    border: "2px solid rgba(147, 197, 253, 0.25)",
    boxShadow:
      "0 8px 32px rgba(14, 165, 233, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.5) inset",
    marginBottom: "24px",
    overflow: "hidden",
    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  },
  queryBox: {
    width: "100%",
    padding: "18px 22px",
    borderRadius: "16px",
    border: "2px solid #d0d5d6ff",
    fontSize: "16px",
    fontFamily: "inherit",
    resize: "vertical",
    minHeight: "100px",
    transition: "all 0.3s ease",
    outline: "none",
    marginTop: "8px",
    lineHeight: 1.6,
    background: "#ffffff",
  },
  button: {
    padding: "12px 24px",
    borderRadius: "8px",
    border: "none",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "15px",
    fontWeight: "600",
    transition: "all 0.3s ease",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
  primaryButton: {
    background: "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
    color: "white",
  },
  stepCard: {
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    border: "2px solid #e2e8f0",
    marginBottom: "16px",
    overflow: "hidden",
    transition: "all 0.3s ease",
  },
  stepHeader: {
    padding: "16px 20px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    cursor: "pointer",
    background: "linear-gradient(180deg, #fafbfc 0%, #ffffff 100%)",
  },
  stepContent: {
    padding: "20px",
    borderTop: "1px solid #f1f5f9",
  },
  toolName: {
    display: "inline-block",
    padding: "4px 10px",
    borderRadius: "6px",
    backgroundColor: "#f0f9ff",
    color: "#0369a1",
    fontFamily: "monospace",
    fontSize: "13px",
    fontWeight: "600",
    marginBottom: "12px",
  },
  resultBox: {
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    padding: "16px",
    marginTop: "16px",
  },
  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "6px 12px",
    borderRadius: "20px",
    fontSize: "13px",
    fontWeight: "600",
  },
};

/**
 * AVAILABLE_TOOLS is what your prompt uses to constrain step.tool values.
 * Keep this minimal + correct (function names only).
 */
export const AVAILABLE_TOOLS = [
  {
    name: "AlzMongo",
    type: "API",
    description:
      "AWS API Gateway wrappers over MongoDB on EC2 (read-only). Supports DB discovery, collection discovery, generic find queries, and curated Alzheimer’s analytics helpers.",
    functions: [
      "alz_mongo_list_databases",
      "alz_mongo_list_collections",
      "alz_mongo_find",
      "alz_mongo_sample_expression_covariates",
      "alz_mongo_scdeg_top_genes",
    ],
  },
];

/**
 * Optional: richer metadata used by an executor/router to map tool -> (method, url).
 * If you build a server-side dispatcher, this is the clean source of truth.
 */
export const AVAILABLE_TOOL_SPECS = [
  {
    apiName: "alz_mongo_list_databases",
    method: "GET",
    path: "/mongo/databases",
    purpose: "Return a list of all MongoDB database names visible to the api_reader user.",
    requiredParams: [],
  },
  {
    apiName: "alz_mongo_list_collections",
    method: "GET",
    path: "/mongo/collections",
    purpose: "Return the list of collection names for a given database.",
    requiredParams: ["db"],
  },
  {
    apiName: "alz_mongo_find",
    method: "POST",
    path: "/mongo/find",
    purpose: "Run a generic MongoDB find query on a collection and return matching documents.",
    requiredParams: ["db", "collection"],
  },
  {
    apiName: "alz_mongo_sample_expression_covariates",
    method: "POST",
    path: "/mongo/sample-expression-covariates",
    purpose:
      "Fetch subject/sample-level gene expression and corresponding clinical/pathological covariates for a given gene and study.",
    requiredParams: ["db", "gene", "study"],
  },
  {
    apiName: "alz_mongo_scdeg_top_genes",
    method: "POST",
    path: "/mongo/scdeg/top-genes",
    purpose:
      "Return top differentially expressed genes from rnaseq_deg_disease_genes with filters, ordered by |logFC|.",
    requiredParams: ["db"],
  },
  {
  apiName: "alz_mongo_bulkdeg_by_gene",
  method: "POST",
  path: "/mongo/bulkdeg/by-gene",
  purpose:
    "Return all differential expression records for a single gene across brain regions and studies.",
  paramsSchema: {
    db: "string (required)",
    gene: "string (required, HGNC symbol)",
    contrast: "string (required, e.g. AD-NCI)",
    pmax: "float (optional, default 1)",
    direction: `"up" | "down" | "both" (optional, default "both")`,
    sort: `"pvalue" | "logFC" (optional)`
  },
}
];

export const DRUG_NAME_BANK = [
  "Donepezil",
  "Rivastigmine",
  "Galantamine",
  "Memantine",
  "Lecanemab",
  "Donanemab",
  "Aducanumab",
  "Gantenerumab",
  "Solanezumab",
  "Gosuranemab",
  "Semorinemab",
  "Tilavonemab",
  "Pexidartinib",
  "BLZ945",
  "Masitinib",
  "Ibrutinib",
  "Fenebrutinib",
  "Baricitinib",
  "Ruxolitinib",
  "Tofacitinib",
  "Ibudilast",
  "Minocycline",
  "Pioglitazone",
  "Nilotinib",
  "Sargramostim",
  "AL002",
];
